import { Route } from 'react-router-dom';
import { navModule } from './modules/nav';
import { apisModule, techRadarApiModule } from './modules/apis';
import { themeModule } from './modules/theme';
import { authModule } from './modules/auth';
import { appRootModule } from './modules/app-root';
import { homeModule } from './modules/home';
import { pageLayoutModule } from './modules/page-layout';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import { FlatRoutes } from '@backstage/core-app-api';
import {
  ExplorePage,
  explorePlugin,
} from '@backstage-community/plugin-explore';
import { explorePage } from './components/explore/ExplorePage';
import qetaPlugin from '@drodil/backstage-plugin-qeta';
import * as plugins from './plugins';
import { TechInsightsPage } from '@internal/backstage-plugin-tech-insights';
import { InfraWalletPage } from '@electrolux-oss/plugin-infrawallet';
import * as Sentry from '@sentry/react';
import { TestkubePage } from '@internal/backstage-plugin-testkube';
import { AgentgatewayKeysPage } from '@internal/backstage-plugin-agentgateway-keys';
import { CorootPage, OneUptimePage } from './components/apps';
import { ReportPortalGlobalPage } from '@backstage-community/plugin-report-portal';
import { GitOpsManifestUpdaterExtension } from '@terasky/backstage-plugin-gitops-manifest-updater';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { apiDocsModule } from './modules/apiDocs';

// ------------------------------------------------------------------
// New Frontend System (NFS) plugin imports
// Plugins that already support the new frontend system via /alpha exports
// are imported here and added directly to the features array.
// ------------------------------------------------------------------
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import catalogImportNfsPlugin from '@backstage/plugin-catalog-import/alpha';
import apiDocsNfsPlugin from '@backstage/plugin-api-docs/alpha';
import mcpChatPlugin from '@backstage-community/plugin-mcp-chat/alpha';
import toolboxPlugin from '@drodil/backstage-plugin-toolbox';
import templateDesignerPlugin from '@tduniec/plugin-template-designer/alpha';
import homePlugin from '@backstage/plugin-home/alpha';

// ------------------------------------------------------------------
// Convert legacy createApp options – only plugins remain here.
// APIs and themes have been migrated to NFS extensions in modules/.
// ------------------------------------------------------------------
const convertedOptionsModule = convertLegacyAppOptions({
  plugins: Object.values(plugins),
});

// ------------------------------------------------------------------
// Legacy routes – plugins that do not yet support the new frontend
// system, or require custom children/props that are easier to keep
// as legacy routes for now.
// ------------------------------------------------------------------
const routes = (
  <FlatRoutes>
    <Route path="/catalog" element={<CatalogIndexPage pagination />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    />
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <GitOpsManifestUpdaterExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/explore" element={<ExplorePage />}>
      {explorePage}
    </Route>
    <Route
      path="/tech-radar"
      element={
        <TechRadarPage
          id="dcs"
          width={1500}
          height={800}
          title="DCS Tech-Radar"
          subtitle="Overview of technologies to choose"
          pageTitle="Tech-Radar"
        />
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    {/* Internal / no-NFS plugins — kept as legacy routes */}
    <Route path="/score-board" element={<TechInsightsPage />} />
    <Route path="/infrawallet" element={<InfraWalletPage />} />
    <Route path="/testkube" element={<TestkubePage />} />
    <Route path="/ai-gateway" element={<AgentgatewayKeysPage />} />
    <Route path="/coroot" element={<CorootPage />} />
    <Route path="/report-incident" element={<OneUptimePage />} />
    <Route path="/report-portal" element={<ReportPortalGlobalPage />} />
  </FlatRoutes>
);

// ------------------------------------------------------------------
// Convert the legacy FlatRoutes into features for the new system.
// Passing FlatRoutes directly avoids layout/nav overrides so that
// the NFS app layout and navModule are used instead.
//
// The entity page conversion discovers components from EntityPage.tsx
// and creates legacy plugins containing only entity cards (no page
// routes). These override the NFS versions (auto-discovered via
// packages: all) that include the actual pages. Filter them out so
// the NFS plugins work correctly.
// ------------------------------------------------------------------
const NFS_PLUGIN_IDS_FROM_ENTITY_PAGE = new Set([
  'api-docs',
  'catalog-graph',
  'azureDevOps',
]);
const convertedRoutes = convertLegacyAppRoot(routes, { entityPage }).filter(
  f =>
    !(
      f.$$type === '@backstage/FrontendPlugin' &&
      NFS_PLUGIN_IDS_FROM_ENTITY_PAGE.has(f.id)
    ),
);

// ------------------------------------------------------------------
// Sentry initialisation
// ------------------------------------------------------------------
Sentry.init({
  dsn: 'https://e5090362d3ee6ad65a0c4e23d1cfdce0@sentry.enspire.danfoss.com/30',
  integrations: [],
});

// ------------------------------------------------------------------
// Create the app with the new frontend system.
//
// - NFS-ready plugins are added directly to features.
// - Legacy options and root tree are converted via compat helpers.
// - bindRoutes wires up cross-plugin navigation.
// ------------------------------------------------------------------
const app = createApp({
  features: [
    navModule,
    // NFS plugins (standalone pages with alpha exports)
    userSettingsPlugin,
    notificationsPlugin,
    catalogImportNfsPlugin,
    apiDocsNfsPlugin,
    mcpChatPlugin,
    toolboxPlugin,
    templateDesignerPlugin,
    homePlugin,
    homeModule,
    qetaPlugin,
    apiDocsModule,

    // App modules: APIs, theme, auth (sign-in + no-op APIs), app-root wrappers
    apisModule,
    techRadarApiModule,
    themeModule,
    authModule,
    appRootModule,
    pageLayoutModule,

    // Converted legacy options (plugins only – APIs & themes migrated above)
    convertedOptionsModule,

    // Converted legacy routes and entity pages (api-docs plugin filtered out
    // above to avoid overriding the NFS apiDocsNfsPlugin)
    ...convertedRoutes,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(explorePlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
  },
});

export default app.createRoot();
